import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getTelegramStatus, requestTelegramLink } from '../services/telegramService';

const TelegramOnboardingPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [telegramStatus, setTelegramStatus] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [linkingInProgress, setLinkingInProgress] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [currentStep, setCurrentStep] = useState(1);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    checkTelegramStatus();
  }, [user, navigate]);

  const checkTelegramStatus = async () => {
    try {
      const status = await getTelegramStatus();
      setTelegramStatus(status);
      if (status.linked) {
        // Rediriger vers le profil si déjà lié
        navigate('/profile');
        return;
      }
    } catch (error) {
      console.error('Erreur lors de la vérification du statut:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRequestLink = async () => {
    if (!user.phone) {
      alert("Aucun numéro de téléphone trouvé dans votre profil");
      return;
    }

    try {
      setLinkingInProgress(true);
      const response = await requestTelegramLink(user.phone);
      setVerificationCode(response.code);
      setCurrentStep(3);
    } catch (error) {
      console.error("Erreur lors de la demande de liaison:", error);
      alert("Erreur lors de la génération du code. Veuillez réessayer.");
    } finally {
      setLinkingInProgress(false);
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <div className="text-lg">Chargement...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="bg-blue-500 bg-opacity-10 rounded-full w-24 h-24 flex items-center justify-center mx-auto mb-6">
            <svg className="w-12 h-12 text-blue-500" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 0C5.374 0 0 5.373 0 12s5.374 12 12 12 12-5.373 12-12S18.626 0 12 0zm5.568 8.16c-.169 1.858-.896 6.728-.896 6.728-.302 1.507-1.124 1.771-1.621 1.771-.823 0-1.011-1.002-1.011-1.002l-2.262-9.485s-.175-.97.556-.97c.73 0 1.066.645 1.066.645l1.781 7.058s.096.466.553.466.457-.463.457-.463l1.376-6.748z"/>
            </svg>
          </div>
          <h1 className="text-4xl font-bold text-slate mb-4">
            Connectez-vous avec Telegram 🚀
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Recevez des notifications instantanées et ne manquez plus jamais une mise à jour importante de BeninAgri
          </p>
        </div>

        {/* Progression */}
        <div className="mb-12">
          <div className="flex items-center justify-center space-x-4">
            <div className={`flex items-center ${currentStep >= 1 ? 'text-blue-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${currentStep >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}>
                1
              </div>
              <span className="ml-2 hidden md:block">Découvrir</span>
            </div>
            <div className="h-0.5 w-16 bg-gray-300"></div>
            <div className={`flex items-center ${currentStep >= 2 ? 'text-blue-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${currentStep >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}>
                2
              </div>
              <span className="ml-2 hidden md:block">Configurer</span>
            </div>
            <div className="h-0.5 w-16 bg-gray-300"></div>
            <div className={`flex items-center ${currentStep >= 3 ? 'text-blue-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${currentStep >= 3 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}>
                3
              </div>
              <span className="ml-2 hidden md:block">Activer</span>
            </div>
          </div>
        </div>

        {/* Étape 1: Découvrir les avantages */}
        {currentStep === 1 && (
          <div className="space-y-8">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-slate mb-4">Pourquoi Telegram ?</h2>
              <p className="text-gray-600">Découvrez tous les avantages de lier votre compte Telegram à BeninAgri</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-white rounded-lg shadow-md p-6 text-center">
                <div className="bg-green-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold mb-2">Notifications instantanées</h3>
                <p className="text-gray-600">Recevez immédiatement les notifications importantes directement sur votre téléphone</p>
              </div>

              <div className="bg-white rounded-lg shadow-md p-6 text-center">
                <div className="bg-blue-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold mb-2">Sécurité renforcée</h3>
                <p className="text-gray-600">Récupérez facilement l'accès à votre compte via Telegram en cas d'oubli de mot de passe</p>
              </div>

              <div className="bg-white rounded-lg shadow-md p-6 text-center">
                <div className="bg-purple-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4V2a1 1 0 011-1h8a1 1 0 011 1v2m-9 4h10m-10 0V18a2 2 0 002 2h6a2 2 0 002-2V8" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold mb-2">Offres exclusives</h3>
                <p className="text-gray-600">Soyez les premiers informés des promotions et offres spéciales de nos agriculteurs</p>
              </div>
            </div>

            <div className="bg-blue-50 rounded-lg p-6 mb-8">
              <h3 className="text-lg font-semibold mb-4 text-blue-800">Que recevrez-vous ?</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <ul className="space-y-2">
                  <li className="flex items-center text-blue-700">
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Confirmation de commandes
                  </li>
                  <li className="flex items-center text-blue-700">
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Statuts de livraison
                  </li>
                  <li className="flex items-center text-blue-700">
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Nouveaux produits
                  </li>
                </ul>
                <ul className="space-y-2">
                  <li className="flex items-center text-blue-700">
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Promotions spéciales
                  </li>
                  <li className="flex items-center text-blue-700">
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Alertes de sécurité
                  </li>
                  <li className="flex items-center text-blue-700">
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Mises à jour de la plateforme
                  </li>
                </ul>
              </div>
            </div>

            <div className="text-center">
              <button
                onClick={() => setCurrentStep(2)}
                className="bg-blue-600 text-white px-8 py-3 rounded-lg font-medium hover:bg-blue-700 transition inline-flex items-center"
              >
                Commencer la configuration
                <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* Étape 2: Configuration */}
        {currentStep === 2 && (
          <div className="space-y-8">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-slate mb-4">Configuration</h2>
              <p className="text-gray-600">Nous allons maintenant lier votre compte à Telegram</p>
            </div>

            <div className="bg-white rounded-lg shadow-md p-8 max-w-2xl mx-auto">
              <div className="text-center mb-6">
                <div className="bg-blue-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold mb-2">Votre numéro de téléphone</h3>
                <p className="text-gray-600 mb-4">
                  Nous utiliserons votre numéro de téléphone associé à votre compte pour la liaison
                </p>
                <div className="bg-gray-50 rounded-lg p-4">
                  <span className="text-lg font-mono font-semibold">{user?.phone || 'Non renseigné'}</span>
                </div>
              </div>

              {user?.phone ? (
                <div className="text-center space-y-4">
                  <p className="text-sm text-gray-600">
                    En cliquant sur "Générer le code", nous créerons un code unique que vous devrez envoyer à notre bot Telegram.
                  </p>
                  <button
                    onClick={handleRequestLink}
                    disabled={linkingInProgress}
                    className="bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {linkingInProgress ? 'Génération...' : 'Générer le code de liaison'}
                  </button>
                </div>
              ) : (
                <div className="text-center">
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                    <p className="text-red-800">
                      Aucun numéro de téléphone trouvé dans votre profil. 
                      Veuillez contacter l'administrateur pour ajouter votre numéro.
                    </p>
                  </div>
                  <Link
                    to="/profile"
                    className="bg-gray-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-gray-700 transition"
                  >
                    Retour au profil
                  </Link>
                </div>
              )}
            </div>

            <div className="text-center">
              <button
                onClick={() => setCurrentStep(1)}
                className="text-gray-600 hover:text-gray-800 transition"
              >
                ← Retour
              </button>
            </div>
          </div>
        )}

        {/* Étape 3: Activation */}
        {currentStep === 3 && verificationCode && (
          <div className="space-y-8">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-slate mb-4">Presque terminé ! 🎉</h2>
              <p className="text-gray-600">Envoyez le code ci-dessous à notre bot Telegram</p>
            </div>

            <div className="bg-white rounded-lg shadow-md p-8 max-w-2xl mx-auto">
              <div className="text-center mb-6">
                <div className="bg-green-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold mb-4">Code de vérification généré</h3>
                
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
                  <p className="text-blue-800 font-semibold mb-2">Votre code :</p>
                  <div className="text-3xl font-mono font-bold text-blue-900 mb-4">{verificationCode}</div>
                  <p className="text-blue-700 text-sm">
                    Copiez ce code et envoyez-le à <a href="https://t.me/beninagri_official_bot" target="_blank" rel="noopener noreferrer" className="font-semibold text-blue-600 underline">@beninagri_official_bot</a> sur Telegram
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <h4 className="font-semibold">Instructions détaillées :</h4>
                <ol className="list-decimal list-inside space-y-2 text-sm text-gray-600">
                  <li>Ouvrez l'application Telegram sur votre téléphone</li>
                  <li>Recherchez <a href="https://t.me/beninagri_official_bot" target="_blank" rel="noopener noreferrer" className="font-semibold text-blue-600 underline">@beninagri_official_bot</a></li>
                  <li>Démarrez une conversation avec le bot</li>
                  <li>Envoyez exactement ce code : <code className="bg-gray-100 px-2 py-1 rounded">{verificationCode}</code></li>
                  <li>Le bot confirmera que votre compte est lié</li>
                  <li>Vous commencerez à recevoir des notifications !</li>
                </ol>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-6">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-yellow-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <div className="text-yellow-800 text-sm">
                      <strong>Important :</strong> Ce code expire dans 15 minutes. Si vous avez des difficultés, 
                      vous pouvez revenir sur cette page et générer un nouveau code.
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="text-center space-y-4">
              <p className="text-gray-600">Une fois le code envoyé, vous pouvez fermer cette page.</p>
              <div className="space-x-4">
                <Link
                  to="/profile"
                  className="bg-green-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-green-700 transition"
                >
                  Aller au profil
                </Link>
                <Link
                  to="/"
                  className="bg-gray-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-gray-700 transition"
                >
                  Retour à l'accueil
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TelegramOnboardingPage;